import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet, apiUpload } from '../services/api';
import {
  Plus,
  Search,
  Receipt,
  Upload,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Paperclip,
} from 'lucide-react';

interface Expense {
  id: string;
  expense_number: string;
  vendor_id: string | null;
  vendor_name: string | null;
  category_id: string | null;
  category_name: string | null;
  status: string;
  expense_date: string;
  currency: string;
  total: number;
  has_attachment: boolean;
}

interface PaginatedResponse {
  items: Expense[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-500/10 text-yellow-400',
  approved: 'bg-blue-500/10 text-blue-400',
  paid: 'bg-green-500/10 text-green-400',
  rejected: 'bg-red-500/10 text-red-400',
  cancelled: 'bg-gray-500/10 text-gray-500',
};

function formatCurrency(amount: number, currency = 'ZAR'): string {
  const symbols: Record<string, string> = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || 'R';
  return `${symbol}${amount.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-ZA');
}

export default function Expenses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const page = parseInt(searchParams.get('page') || '1');
  const status = searchParams.get('status') || '';

  const fetchExpenses = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('page_size', '20');
      if (search) params.set('search', search);
      if (status) params.set('status', status);

      const data = await apiGet<PaginatedResponse>(`/api/expenses?${params}`);
      setExpenses(data.items);
      setTotalPages(data.total_pages);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to fetch expenses:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [page, status, search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleStatusFilter = (newStatus: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (newStatus) {
      params.set('status', newStatus);
    } else {
      params.delete('status');
    }
    setSearchParams(params);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      await apiUpload('/api/expenses/upload', file, { process_ocr: 'true' });
      fetchExpenses();
    } catch (err) {
      console.error('Failed to upload expense:', err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const statusFilters = ['', 'pending', 'approved', 'paid', 'rejected', 'cancelled'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Expenses</h1>
          <p className="text-gray-400">{total} total expenses</p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center gap-2 px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
          >
            <Upload className="w-5 h-5" />
            {isUploading ? 'Uploading...' : 'Upload Receipt'}
          </button>
          <Link
            to="/admin/expenses/new"
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            New Expense
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search expenses..."
              className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </form>

        <div className="flex gap-2 flex-wrap">
          {statusFilters.map((s) => (
            <button
              key={s}
              onClick={() => handleStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${
                status === s
                  ? 'bg-primary-500 text-white'
                  : 'bg-dark-800 text-gray-400 hover:text-white'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-dark-900 rounded-xl border border-dark-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-800">
                <th className="text-left p-4 text-sm font-medium text-gray-400">Expense</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Vendor</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Category</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Status</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Date</th>
                <th className="text-right p-4 text-sm font-medium text-gray-400">Amount</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-800">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-500">
                    No expenses found
                  </td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-dark-800/50">
                    <td className="p-4">
                      <Link
                        to={`/admin/expenses/${expense.id}`}
                        className="flex items-center gap-2 text-white hover:text-primary-500"
                      >
                        <Receipt className="w-4 h-4" />
                        {expense.expense_number}
                        {expense.has_attachment && (
                          <Paperclip className="w-3 h-3 text-gray-500" />
                        )}
                      </Link>
                    </td>
                    <td className="p-4 text-gray-300">
                      {expense.vendor_name || '-'}
                    </td>
                    <td className="p-4 text-gray-300">
                      {expense.category_name || '-'}
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          statusColors[expense.status] || statusColors.pending
                        }`}
                      >
                        {expense.status}
                      </span>
                    </td>
                    <td className="p-4 text-gray-400">{formatDate(expense.expense_date)}</td>
                    <td className="p-4 text-right text-white">
                      {formatCurrency(expense.total, expense.currency)}
                    </td>
                    <td className="p-4 text-right">
                      <Link
                        to={`/admin/expenses/${expense.id}`}
                        className="p-2 hover:bg-dark-700 rounded-lg inline-block"
                      >
                        <MoreHorizontal className="w-4 h-4 text-gray-400" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-dark-800">
            <p className="text-sm text-gray-400">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set('page', (page - 1).toString());
                  setSearchParams(params);
                }}
                disabled={page <= 1}
                className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              >
                <ChevronLeft className="w-5 h-5 text-gray-400" />
              </button>
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set('page', (page + 1).toString());
                  setSearchParams(params);
                }}
                disabled={page >= totalPages}
                className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              >
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
